import { Fragment, type ReactNode, createElement, useEffect } from "react";
import {
  DEFAULT_MODEL_BY_PROVIDER,
  type OrchestrationEvent,
  type ProviderKind,
  ThreadId,
  type OrchestrationThreadDetail,
  type OrchestrationThreadIndex,
  type OrchestrationThreadSummary,
  type OrchestrationSessionStatus,
} from "@t3tools/contracts";
import {
  getModelOptions,
  normalizeModelSlug,
  resolveModelSlug,
  resolveModelSlugForProvider,
} from "@t3tools/shared/model";
import { create } from "zustand";
import { type ChatMessage, type Project, type Thread } from "./types";
import { Debouncer } from "@tanstack/react-pacer";
import { IS_OPERATORS_APP } from "./operators";
import {
  buildPlanMarkdownFromPlanUpdatePayload,
  deriveTimelineActivePromptAnchor,
  insertChatMessageForTimeline,
  orderChatMessagesForTimeline,
} from "./session-logic";
import { resolveNewThreadModel } from "./appSettings";

// ── State ────────────────────────────────────────────────────────────

export interface AppState {
  projects: Project[];
  threads: Thread[];
  threadsHydrated: boolean;
  hydratedThreadDetails: ReadonlySet<ThreadId>;
  threadIndexRevision: number;
  threadIndexNextCursor?: string | null | undefined;
  archivingThreadIds: ReadonlySet<ThreadId>;
}

const PERSISTED_STATE_KEY = "t3code:renderer-state:v8";
const LEGACY_PERSISTED_STATE_KEYS = [
  "t3code:renderer-state:v7",
  "t3code:renderer-state:v6",
  "t3code:renderer-state:v5",
  "t3code:renderer-state:v4",
  "t3code:renderer-state:v3",
  "codething:renderer-state:v4",
  "codething:renderer-state:v3",
  "codething:renderer-state:v2",
  "codething:renderer-state:v1",
] as const;

const initialState: AppState = {
  projects: [],
  threads: [],
  threadsHydrated: false,
  hydratedThreadDetails: new Set<ThreadId>(),
  threadIndexRevision: 0,
  archivingThreadIds: new Set<ThreadId>(),
};
const persistedExpandedProjectCwds = new Set<string>();
const persistedProjectOrderCwds: string[] = [];

// ── Persist helpers ──────────────────────────────────────────────────

function readPersistedState(): AppState {
  if (typeof window === "undefined") return initialState;
  try {
    const raw = window.localStorage.getItem(PERSISTED_STATE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as {
      expandedProjectCwds?: string[];
      projectOrderCwds?: string[];
    };
    persistedExpandedProjectCwds.clear();
    persistedProjectOrderCwds.length = 0;
    for (const cwd of parsed.expandedProjectCwds ?? []) {
      if (typeof cwd === "string" && cwd.length > 0) {
        persistedExpandedProjectCwds.add(cwd);
      }
    }
    for (const cwd of parsed.projectOrderCwds ?? []) {
      if (typeof cwd === "string" && cwd.length > 0 && !persistedProjectOrderCwds.includes(cwd)) {
        persistedProjectOrderCwds.push(cwd);
      }
    }
    return { ...initialState };
  } catch {
    return initialState;
  }
}

let legacyKeysCleanedUp = false;

function persistState(state: AppState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      PERSISTED_STATE_KEY,
      JSON.stringify({
        expandedProjectCwds: state.projects
          .filter((project) => project.expanded)
          .map((project) => project.cwd),
        projectOrderCwds: state.projects.map((project) => project.cwd),
      }),
    );
    if (!legacyKeysCleanedUp) {
      legacyKeysCleanedUp = true;
      for (const legacyKey of LEGACY_PERSISTED_STATE_KEYS) {
        window.localStorage.removeItem(legacyKey);
      }
    }
  } catch {
    // Ignore quota/storage errors to avoid breaking chat UX.
  }
}
const debouncedPersistState = new Debouncer(persistState, { wait: 500 });

// ── Pure helpers ──────────────────────────────────────────────────────

function updateThread(
  threads: Thread[],
  threadId: ThreadId,
  updater: (t: Thread) => Thread,
): Thread[] {
  let changed = false;
  const next = threads.map((t) => {
    if (t.id !== threadId) return t;
    const updated = updater(t);
    if (updated !== t) changed = true;
    return updated;
  });
  return changed ? next : threads;
}

function shouldRetainOptimisticOperatorsThread(thread: Thread): boolean {
  return (
    IS_OPERATORS_APP &&
    thread.deletedAt == null &&
    thread.session === null &&
    thread.messages.length === 0 &&
    thread.proposedPlans.length === 0 &&
    thread.turnDiffSummaries.length === 0 &&
    thread.activities.length === 0 &&
    thread.error === null
  );
}

function setThreadArchivingIds(
  archivingThreadIds: ReadonlySet<ThreadId>,
  threadId: ThreadId,
  archiving: boolean,
): ReadonlySet<ThreadId> {
  const isAlreadyArchiving = archivingThreadIds.has(threadId);
  if (isAlreadyArchiving === archiving) {
    return archivingThreadIds;
  }

  const next = new Set(archivingThreadIds);
  if (archiving) {
    next.add(threadId);
  } else {
    next.delete(threadId);
  }
  return next;
}

function pruneArchivingThreadIds(
  archivingThreadIds: ReadonlySet<ThreadId>,
  threads: ReadonlyArray<Thread>,
): ReadonlySet<ThreadId> {
  if (archivingThreadIds.size === 0) {
    return archivingThreadIds;
  }

  const threadsById = new Map(threads.map((thread) => [thread.id, thread] as const));
  const next = new Set<ThreadId>();
  for (const threadId of archivingThreadIds) {
    const thread = threadsById.get(threadId);
    if (thread && thread.deletedAt == null) {
      next.add(threadId);
    }
  }

  if (next.size === archivingThreadIds.size) {
    let changed = false;
    for (const threadId of archivingThreadIds) {
      if (!next.has(threadId)) {
        changed = true;
        break;
      }
    }
    if (!changed) {
      return archivingThreadIds;
    }
  }

  return next;
}

function mapProjectsFromReadModel(
  incoming: OrchestrationThreadIndex["projects"],
  previous: Project[],
): Project[] {
  const previousById = new Map(previous.map((project) => [project.id, project] as const));
  const previousByCwd = new Map(previous.map((project) => [project.cwd, project] as const));
  const previousOrderById = new Map(previous.map((project, index) => [project.id, index] as const));
  const previousOrderByCwd = new Map(
    previous.map((project, index) => [project.cwd, index] as const),
  );
  const persistedOrderByCwd = new Map(
    persistedProjectOrderCwds.map((cwd, index) => [cwd, index] as const),
  );
  const usePersistedOrder = previous.length === 0;

  const mappedProjects = incoming.map((project) => {
    const existing = previousById.get(project.id) ?? previousByCwd.get(project.workspaceRoot);
    return {
      id: project.id,
      name: project.title,
      cwd: project.workspaceRoot,
      model: resolveNewThreadModel(
        existing?.model ??
          resolveModelSlug(project.defaultModel ?? DEFAULT_MODEL_BY_PROVIDER.codex),
      ),
      expanded:
        existing?.expanded ??
        (persistedExpandedProjectCwds.size > 0
          ? persistedExpandedProjectCwds.has(project.workspaceRoot)
          : true),
      scripts: project.scripts.map((script) => ({ ...script })),
    } satisfies Project;
  });

  return mappedProjects
    .map((project, incomingIndex) => {
      const previousIndex =
        previousOrderById.get(project.id) ?? previousOrderByCwd.get(project.cwd);
      const persistedIndex = usePersistedOrder ? persistedOrderByCwd.get(project.cwd) : undefined;
      const orderIndex =
        previousIndex ??
        persistedIndex ??
        (usePersistedOrder ? persistedProjectOrderCwds.length : previous.length) + incomingIndex;
      return { project, incomingIndex, orderIndex };
    })
    .toSorted((a, b) => {
      const byOrder = a.orderIndex - b.orderIndex;
      if (byOrder !== 0) return byOrder;
      return a.incomingIndex - b.incomingIndex;
    })
    .map((entry) => entry.project);
}

function toLegacySessionStatus(
  status: OrchestrationSessionStatus,
): "connecting" | "ready" | "running" | "error" | "closed" {
  switch (status) {
    case "starting":
      return "connecting";
    case "running":
      return "running";
    case "error":
      return "error";
    case "ready":
    case "interrupted":
      return "ready";
    case "idle":
    case "stopped":
      return "closed";
  }
}

function toLegacyProvider(providerName: string | null): ProviderKind {
  if (providerName === "codex") {
    return providerName;
  }
  return "codex";
}

const CODEX_MODEL_SLUGS = new Set<string>(getModelOptions("codex").map((option) => option.slug));

function inferProviderForThreadModel(input: {
  readonly model: string;
  readonly sessionProviderName: string | null;
}): ProviderKind {
  if (input.sessionProviderName === "codex") {
    return input.sessionProviderName;
  }
  const normalizedCodex = normalizeModelSlug(input.model, "codex");
  if (normalizedCodex && CODEX_MODEL_SLUGS.has(normalizedCodex)) {
    return "codex";
  }
  return "codex";
}

function resolveWsHttpOrigin(): string {
  if (typeof window === "undefined") return "";
  const bridgeWsUrl = window.desktopBridge?.getWsUrl?.();
  const envWsUrl = import.meta.env.VITE_WS_URL as string | undefined;
  const wsCandidate =
    typeof bridgeWsUrl === "string" && bridgeWsUrl.length > 0
      ? bridgeWsUrl
      : typeof envWsUrl === "string" && envWsUrl.length > 0
        ? envWsUrl
        : null;
  if (!wsCandidate) return window.location.origin;
  try {
    const wsUrl = new URL(wsCandidate);
    const protocol =
      wsUrl.protocol === "wss:" ? "https:" : wsUrl.protocol === "ws:" ? "http:" : wsUrl.protocol;
    return `${protocol}//${wsUrl.host}`;
  } catch {
    return window.location.origin;
  }
}

function toAttachmentPreviewUrl(rawUrl: string): string {
  if (rawUrl.startsWith("/")) {
    return `${resolveWsHttpOrigin()}${rawUrl}`;
  }
  return rawUrl;
}

function attachmentPreviewRoutePath(attachmentId: string): string {
  return `/attachments/${encodeURIComponent(attachmentId)}`;
}

function mapMessageAttachments(
  attachments: OrchestrationThreadDetail["thread"]["messages"][number]["attachments"],
): ChatMessage["attachments"] | undefined {
  if (attachments == null) {
    return undefined;
  }
  return attachments.map((attachment) => ({
    type: "image" as const,
    id: attachment.id,
    name: attachment.name,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
    previewUrl: toAttachmentPreviewUrl(attachmentPreviewRoutePath(attachment.id)),
  }));
}

function mapUploadedFiles(
  uploadedFiles: OrchestrationThreadDetail["thread"]["messages"][number]["uploadedFiles"],
): ChatMessage["uploadedFiles"] | undefined {
  if (uploadedFiles == null) {
    return undefined;
  }
  return uploadedFiles.map((file) => ({ ...file }));
}

function mapReadModelMessage(
  message: OrchestrationThreadDetail["thread"]["messages"][number],
): ChatMessage {
  const attachments = mapMessageAttachments(message.attachments);
  const uploadedFiles = mapUploadedFiles(message.uploadedFiles);
  return {
    id: message.id,
    role: message.role,
    ...(message.author ? { author: { ...message.author } } : {}),
    text: message.text,
    turnId: message.turnId,
    createdAt: message.createdAt,
    streaming: message.streaming,
    ...(message.streaming ? {} : { completedAt: message.updatedAt }),
    ...(attachments !== undefined ? { attachments } : {}),
    ...(uploadedFiles !== undefined ? { uploadedFiles } : {}),
  };
}

type ThreadMessageSentEvent = Extract<OrchestrationEvent, { type: "thread.message-sent" }>;

function updateLatestTurnFromAssistantMessage(
  latestTurn: Thread["latestTurn"],
  payload: ThreadMessageSentEvent["payload"],
): Thread["latestTurn"] {
  if (payload.role !== "assistant" || payload.turnId === null) {
    return latestTurn;
  }
  if (latestTurn == null) {
    return {
      turnId: payload.turnId,
      state: payload.streaming ? "running" : "completed",
      requestedAt: payload.createdAt,
      startedAt: payload.createdAt,
      completedAt: payload.streaming ? null : payload.updatedAt,
      assistantMessageId: payload.messageId,
    };
  }
  if (latestTurn.turnId !== payload.turnId) {
    return latestTurn;
  }
  return {
    ...latestTurn,
    assistantMessageId: payload.messageId,
    state: payload.streaming
      ? latestTurn.state
      : latestTurn.state === "interrupted"
        ? "interrupted"
        : latestTurn.state === "error"
          ? "error"
          : "completed",
    startedAt: latestTurn.startedAt ?? payload.createdAt,
    requestedAt: latestTurn.requestedAt ?? payload.createdAt,
    completedAt: payload.streaming
      ? latestTurn.completedAt
      : (latestTurn.completedAt ?? payload.updatedAt),
  };
}

function applyThreadMessageSent(thread: Thread, event: ThreadMessageSentEvent): Thread {
  const { payload } = event;
  const existingMessage = thread.messages.find((message) => message.id === payload.messageId);
  const nextText =
    existingMessage && payload.streaming
      ? `${existingMessage.text}${payload.text}`
      : existingMessage && payload.text.length === 0
        ? existingMessage.text
        : payload.text;
  const nextAttachments =
    payload.attachments !== undefined
      ? mapMessageAttachments(payload.attachments)
      : existingMessage?.attachments;
  const nextUploadedFiles =
    payload.uploadedFiles !== undefined
      ? mapUploadedFiles(payload.uploadedFiles)
      : existingMessage?.uploadedFiles;
  const nextAuthor = payload.author ?? existingMessage?.author;
  const nextMessage: ChatMessage = {
    id: payload.messageId,
    role: payload.role,
    ...(nextAuthor ? { author: { ...nextAuthor } } : {}),
    text: nextText,
    turnId: payload.turnId,
    createdAt: existingMessage?.createdAt ?? payload.createdAt,
    streaming: payload.streaming,
    ...(payload.streaming ? {} : { completedAt: payload.updatedAt }),
    ...(nextAttachments !== undefined ? { attachments: nextAttachments } : {}),
    ...(nextUploadedFiles !== undefined ? { uploadedFiles: nextUploadedFiles } : {}),
  };
  const messages = existingMessage
    ? thread.messages.map((message) => (message.id === payload.messageId ? nextMessage : message))
    : insertChatMessageForTimeline(thread.messages, nextMessage);
  const latestTurn = updateLatestTurnFromAssistantMessage(thread.latestTurn, payload);
  if (messages === thread.messages && latestTurn === thread.latestTurn) {
    return thread;
  }
  return {
    ...thread,
    messages,
    latestTurn,
  };
}

type ThreadActivityAppendedEvent = Extract<
  OrchestrationEvent,
  { type: "thread.activity-appended" }
>;
type ThreadProposedPlanUpsertedEvent = Extract<
  OrchestrationEvent,
  { type: "thread.proposed-plan-upserted" }
>;

function applyThreadActivityAppended(thread: Thread, event: ThreadActivityAppendedEvent): Thread {
  const nextActivity = { ...event.payload.activity };
  const existingIndex = thread.activities.findIndex((activity) => activity.id === nextActivity.id);
  const activities =
    existingIndex === -1
      ? [...thread.activities, nextActivity]
      : thread.activities.map((activity, index) =>
          index === existingIndex ? nextActivity : activity,
        );

  if (nextActivity.kind !== "turn.plan.updated") {
    return activities === thread.activities ? thread : { ...thread, activities };
  }

  const planId = `plan:${nextActivity.id}`;
  const planMarkdown = buildPlanMarkdownFromPlanUpdatePayload(nextActivity.payload);
  const proposedPlans =
    planMarkdown !== null
      ? upsertProposedPlan(thread.proposedPlans, {
          id: planId,
          turnId: nextActivity.turnId,
          planMarkdown,
          createdAt: nextActivity.createdAt,
          updatedAt: nextActivity.createdAt,
        })
      : thread.proposedPlans.filter((plan) => plan.id !== planId);

  return activities === thread.activities && proposedPlans === thread.proposedPlans
    ? thread
    : { ...thread, activities, proposedPlans };
}

function applyThreadProposedPlanUpserted(
  thread: Thread,
  event: ThreadProposedPlanUpsertedEvent,
): Thread {
  const nextPlan = { ...event.payload.proposedPlan };
  const proposedPlans = upsertProposedPlan(thread.proposedPlans, nextPlan);

  return proposedPlans === thread.proposedPlans ? thread : { ...thread, proposedPlans };
}

function upsertProposedPlan(
  proposedPlans: Thread["proposedPlans"],
  nextPlan: Thread["proposedPlans"][number],
): Thread["proposedPlans"] {
  const existingIndex = proposedPlans.findIndex((plan) => plan.id === nextPlan.id);
  if (existingIndex === -1) {
    return [...proposedPlans, nextPlan];
  }

  return proposedPlans.map((plan, index) => (index === existingIndex ? nextPlan : plan));
}

// ── Pure state transition functions ────────────────────────────────────

function mapThreadSummary(thread: OrchestrationThreadSummary, existing?: Thread): Thread {
  const deletedAt = thread.deletedAt ?? (IS_OPERATORS_APP ? (existing?.deletedAt ?? null) : null);
  return {
    id: thread.id,
    codexThreadId: null,
    projectId: thread.projectId,
    title: thread.title,
    model: resolveModelSlugForProvider(
      inferProviderForThreadModel({
        model: thread.model,
        sessionProviderName: thread.session?.providerName ?? null,
      }),
      thread.model,
    ),
    runtimeMode: thread.runtimeMode,
    interactionMode: thread.interactionMode,
    session: thread.session
      ? {
          provider: toLegacyProvider(thread.session.providerName),
          status: toLegacySessionStatus(thread.session.status),
          orchestrationStatus: thread.session.status,
          activeTurnId: thread.session.activeTurnId ?? undefined,
          createdAt: thread.session.updatedAt,
          updatedAt: thread.session.updatedAt,
          ...(thread.session.lastError ? { lastError: thread.session.lastError } : {}),
        }
      : null,
    messages: existing?.messages ?? [],
    proposedPlans: existing?.proposedPlans ?? [],
    error: thread.session?.lastError ?? null,
    createdAt: thread.createdAt,
    latestTurn: thread.latestTurn,
    turns: existing?.turns ?? [],
    lastVisitedAt: existing?.lastVisitedAt ?? thread.updatedAt,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
    turnDiffSummaries: existing?.turnDiffSummaries ?? [],
    activities: existing?.activities ?? [],
    pendingRequests: thread.pendingRequests ?? existing?.pendingRequests,
    deletedAt,
  };
}

export function syncThreadIndex(
  state: AppState,
  index: OrchestrationThreadIndex,
  options: { append?: boolean; updateCursor?: boolean } = {},
): AppState {
  if (index.revision < state.threadIndexRevision) {
    return state;
  }
  const projects = mapProjectsFromReadModel(
    index.projects.filter((project) => project.deletedAt === null),
    state.projects,
  );
  const existingThreadById = new Map(state.threads.map((thread) => [thread.id, thread] as const));
  const mappedThreads = index.threads
    .filter((thread) => IS_OPERATORS_APP || thread.deletedAt === null)
    .map((thread) => mapThreadSummary(thread, existingThreadById.get(thread.id)));
  const threads =
    IS_OPERATORS_APP && state.threads.length > 0
      ? [
          ...mappedThreads,
          ...state.threads
            .filter(
              (thread) =>
                (index.nextCursor !== undefined ||
                  thread.deletedAt != null ||
                  shouldRetainOptimisticOperatorsThread(thread) ||
                  (!state.threadsHydrated && state.hydratedThreadDetails.has(thread.id))) &&
                !mappedThreads.some((mappedThread) => mappedThread.id === thread.id),
            )
            .map((thread) => ({ ...thread })),
        ]
      : mappedThreads;
  const archivingThreadIds = pruneArchivingThreadIds(state.archivingThreadIds, threads);
  return {
    ...state,
    projects,
    threads,
    threadsHydrated: true,
    hydratedThreadDetails: new Set(
      threads
        .filter((thread) => state.hydratedThreadDetails.has(thread.id))
        .map((thread) => thread.id),
    ),
    threadIndexRevision: index.revision,
    threadIndexNextCursor:
      options.updateCursor !== false &&
      (options.append || !state.threadsHydrated || state.threadIndexNextCursor === undefined)
        ? index.nextCursor
        : state.threadIndexNextCursor,
    archivingThreadIds,
  };
}

export function syncThreadDetail(state: AppState, detail: OrchestrationThreadDetail): AppState {
  if (detail.thread.id !== detail.threadId) {
    throw new Error(`Thread detail mismatch: expected ${detail.threadId}, got ${detail.thread.id}`);
  }

  if (detail.project && detail.project.id !== detail.thread.projectId) {
    throw new Error(`Thread detail project mismatch for ${detail.threadId}`);
  }

  const existing = state.threads.find((thread) => thread.id === detail.threadId);
  const messages = detail.thread.messages.map(mapReadModelMessage);
  const activePromptAnchor = deriveTimelineActivePromptAnchor(messages, detail.thread.latestTurn);
  const nextThread: Thread = {
    ...mapThreadSummary(detail.thread, existing),
    messages: orderChatMessagesForTimeline(messages, { activePromptAnchor }),
    proposedPlans: detail.thread.proposedPlans.map((proposedPlan) => ({
      id: proposedPlan.id,
      turnId: proposedPlan.turnId,
      planMarkdown: proposedPlan.planMarkdown,
      createdAt: proposedPlan.createdAt,
      updatedAt: proposedPlan.updatedAt,
    })),
    turns: (detail.thread.turns ?? []).map((turn) => ({ ...turn })),
    turnDiffSummaries: detail.thread.checkpoints.map((checkpoint) => ({
      turnId: checkpoint.turnId,
      completedAt: checkpoint.completedAt,
      status: checkpoint.status,
      assistantMessageId: checkpoint.assistantMessageId ?? undefined,
      checkpointTurnCount: checkpoint.checkpointTurnCount,
      checkpointRef: checkpoint.checkpointRef,
      files: checkpoint.files.map((file) => ({ ...file })),
    })),
    activities: detail.thread.activities.map((activity) => ({ ...activity })),
  };
  const threads = existing
    ? state.threads.map((thread) => (thread.id === detail.threadId ? nextThread : thread))
    : [nextThread, ...state.threads];
  return {
    ...state,
    // Detail can arrive before the index. Supply its project without marking the
    // global index hydrated or overwriting project state from a newer index.
    projects:
      detail.project && !state.projects.some((project) => project.id === detail.project!.id)
        ? [...state.projects, ...mapProjectsFromReadModel([detail.project], state.projects)]
        : state.projects,
    threads,
    hydratedThreadDetails: new Set([...state.hydratedThreadDetails, detail.threadId]),
    archivingThreadIds: pruneArchivingThreadIds(state.archivingThreadIds, threads),
  };
}

export function applyDomainEvent(state: AppState, event: OrchestrationEvent): AppState {
  switch (event.type) {
    case "thread.message-sent": {
      const threads = updateThread(state.threads, event.payload.threadId, (thread) =>
        applyThreadMessageSent(thread, event),
      );
      return threads === state.threads ? state : { ...state, threads };
    }

    case "thread.activity-appended": {
      const threads = updateThread(state.threads, event.payload.threadId, (thread) =>
        applyThreadActivityAppended(thread, event),
      );
      return threads === state.threads ? state : { ...state, threads };
    }

    case "thread.proposed-plan-upserted": {
      const threads = updateThread(state.threads, event.payload.threadId, (thread) =>
        applyThreadProposedPlanUpserted(thread, event),
      );
      return threads === state.threads ? state : { ...state, threads };
    }

    default:
      return state;
  }
}

export function setThreadArchiving(
  state: AppState,
  threadId: ThreadId,
  archiving: boolean,
): AppState {
  const archivingThreadIds = setThreadArchivingIds(state.archivingThreadIds, threadId, archiving);
  return archivingThreadIds === state.archivingThreadIds ? state : { ...state, archivingThreadIds };
}

export function markThreadVisited(
  state: AppState,
  threadId: ThreadId,
  visitedAt?: string,
): AppState {
  const at = visitedAt ?? new Date().toISOString();
  const visitedAtMs = Date.parse(at);
  const threads = updateThread(state.threads, threadId, (thread) => {
    const previousVisitedAtMs = thread.lastVisitedAt ? Date.parse(thread.lastVisitedAt) : NaN;
    if (
      Number.isFinite(previousVisitedAtMs) &&
      Number.isFinite(visitedAtMs) &&
      previousVisitedAtMs >= visitedAtMs
    ) {
      return thread;
    }
    return { ...thread, lastVisitedAt: at };
  });
  return threads === state.threads ? state : { ...state, threads };
}

export function markThreadUnread(state: AppState, threadId: ThreadId): AppState {
  const threads = updateThread(state.threads, threadId, (thread) => {
    if (!thread.latestTurn?.completedAt) return thread;
    const latestTurnCompletedAtMs = Date.parse(thread.latestTurn.completedAt);
    if (Number.isNaN(latestTurnCompletedAtMs)) return thread;
    const unreadVisitedAt = new Date(latestTurnCompletedAtMs - 1).toISOString();
    if (thread.lastVisitedAt === unreadVisitedAt) return thread;
    return { ...thread, lastVisitedAt: unreadVisitedAt };
  });
  return threads === state.threads ? state : { ...state, threads };
}

export function toggleProject(state: AppState, projectId: Project["id"]): AppState {
  return {
    ...state,
    projects: state.projects.map((p) => (p.id === projectId ? { ...p, expanded: !p.expanded } : p)),
  };
}

export function setProjectExpanded(
  state: AppState,
  projectId: Project["id"],
  expanded: boolean,
): AppState {
  let changed = false;
  const projects = state.projects.map((p) => {
    if (p.id !== projectId || p.expanded === expanded) return p;
    changed = true;
    return { ...p, expanded };
  });
  return changed ? { ...state, projects } : state;
}

export function reorderProjects(
  state: AppState,
  draggedProjectId: Project["id"],
  targetProjectId: Project["id"],
): AppState {
  if (draggedProjectId === targetProjectId) return state;
  const draggedIndex = state.projects.findIndex((project) => project.id === draggedProjectId);
  const targetIndex = state.projects.findIndex((project) => project.id === targetProjectId);
  if (draggedIndex < 0 || targetIndex < 0) return state;
  const projects = [...state.projects];
  const [draggedProject] = projects.splice(draggedIndex, 1);
  if (!draggedProject) return state;
  projects.splice(targetIndex, 0, draggedProject);
  return { ...state, projects };
}

export function setError(state: AppState, threadId: ThreadId, error: string | null): AppState {
  const threads = updateThread(state.threads, threadId, (t) => {
    if (t.error === error) return t;
    return { ...t, error };
  });
  return threads === state.threads ? state : { ...state, threads };
}

export function setThreadBranch(
  state: AppState,
  threadId: ThreadId,
  branch: string | null,
  worktreePath: string | null,
): AppState {
  const threads = updateThread(state.threads, threadId, (t) => {
    if (t.branch === branch && t.worktreePath === worktreePath) return t;
    const cwdChanged = t.worktreePath !== worktreePath;
    return {
      ...t,
      branch,
      worktreePath,
      ...(cwdChanged ? { session: null } : {}),
    };
  });
  return threads === state.threads ? state : { ...state, threads };
}

export function insertOptimisticThread(
  state: AppState,
  input: Pick<
    Thread,
    | "id"
    | "projectId"
    | "title"
    | "model"
    | "runtimeMode"
    | "interactionMode"
    | "createdAt"
    | "branch"
    | "worktreePath"
  >,
): AppState {
  if (state.threads.some((thread) => thread.id === input.id)) {
    return state;
  }

  const thread: Thread = {
    id: input.id,
    codexThreadId: null,
    projectId: input.projectId,
    title: input.title,
    model: input.model,
    runtimeMode: input.runtimeMode,
    interactionMode: input.interactionMode,
    session: null,
    messages: [],
    proposedPlans: [],
    error: null,
    createdAt: input.createdAt,
    latestTurn: null,
    turns: [],
    lastVisitedAt: input.createdAt,
    branch: input.branch,
    worktreePath: input.worktreePath,
    turnDiffSummaries: [],
    activities: [],
    deletedAt: null,
  };

  return {
    ...state,
    threads: [thread, ...state.threads],
    threadsHydrated: true,
    hydratedThreadDetails: new Set([...state.hydratedThreadDetails, input.id]),
  };
}

// ── Zustand store ────────────────────────────────────────────────────

interface AppStore extends AppState {
  syncThreadIndex: (
    index: OrchestrationThreadIndex,
    options?: { append?: boolean; updateCursor?: boolean },
  ) => void;
  syncThreadDetail: (detail: OrchestrationThreadDetail) => void;
  applyDomainEvent: (event: OrchestrationEvent) => void;
  insertOptimisticThread: (
    input: Pick<
      Thread,
      | "id"
      | "projectId"
      | "title"
      | "model"
      | "runtimeMode"
      | "interactionMode"
      | "createdAt"
      | "branch"
      | "worktreePath"
    >,
  ) => void;
  setThreadArchiving: (threadId: ThreadId, archiving: boolean) => void;
  markThreadVisited: (threadId: ThreadId, visitedAt?: string) => void;
  markThreadUnread: (threadId: ThreadId) => void;
  toggleProject: (projectId: Project["id"]) => void;
  setProjectExpanded: (projectId: Project["id"], expanded: boolean) => void;
  reorderProjects: (draggedProjectId: Project["id"], targetProjectId: Project["id"]) => void;
  setError: (threadId: ThreadId, error: string | null) => void;
  setThreadBranch: (threadId: ThreadId, branch: string | null, worktreePath: string | null) => void;
}

export const useStore = create<AppStore>((set) => ({
  ...readPersistedState(),
  syncThreadIndex: (index, options) => set((state) => syncThreadIndex(state, index, options)),
  syncThreadDetail: (detail) => set((state) => syncThreadDetail(state, detail)),
  applyDomainEvent: (event) => set((state) => applyDomainEvent(state, event)),
  insertOptimisticThread: (input) => set((state) => insertOptimisticThread(state, input)),
  setThreadArchiving: (threadId, archiving) =>
    set((state) => setThreadArchiving(state, threadId, archiving)),
  markThreadVisited: (threadId, visitedAt) =>
    set((state) => markThreadVisited(state, threadId, visitedAt)),
  markThreadUnread: (threadId) => set((state) => markThreadUnread(state, threadId)),
  toggleProject: (projectId) => set((state) => toggleProject(state, projectId)),
  setProjectExpanded: (projectId, expanded) =>
    set((state) => setProjectExpanded(state, projectId, expanded)),
  reorderProjects: (draggedProjectId, targetProjectId) =>
    set((state) => reorderProjects(state, draggedProjectId, targetProjectId)),
  setError: (threadId, error) => set((state) => setError(state, threadId, error)),
  setThreadBranch: (threadId, branch, worktreePath) =>
    set((state) => setThreadBranch(state, threadId, branch, worktreePath)),
}));

// Persist state changes with debouncing to avoid localStorage thrashing
useStore.subscribe((state) => debouncedPersistState.maybeExecute(state));

// Flush pending writes synchronously before page unload to prevent data loss.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    debouncedPersistState.flush();
  });
}

export function StoreProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    persistState(useStore.getState());
  }, []);
  return createElement(Fragment, null, children);
}
